import { useState, useEffect, useCallback, useMemo } from 'react';
import { userSession } from '../utils/userSession.js';
import { CalendarIcon, MapPinIcon, ClockIcon, PlusIcon, PencilIcon, TrashIcon, UserIcon, PhoneIcon, EnvelopeIcon, GlobeAltIcon, PhotoIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { useData } from '../context/DataContext.js';
import AddPromoter from './AddPromoter.js';
import EditPromoter from './EditPromoter.js';
import AddEvent from './AddEvent.js';
import EditEvent from './EditEvent.js';
import ConfirmDialog from './ConfirmDialog.js';
import PendingPromoterApplications from './PendingPromoterApplications.js';


export default function UserDashboardPromoter() {
  const [selectedPromoter, setSelectedPromoter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const { 
    events, 
    refreshEvents, 
    promoters, 
    refreshPromoters,
    selectedCountry
  } = useData();
  const [showAddPromoter, setShowAddPromoter] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [showEditPromoter, setShowEditPromoter] = useState(false);
  const promoterDetails = userSession.getUser()?.userTypeDetails?.find(d => d.typeId === 4);
  const promoterData = promoters?.find(p => Number(p.OrganiserID) === Number(promoterDetails?.linkId));

  // Check if user is a promoter and their approval status
  const isPromoter = userSession.hasUserType(4);
  const isApproved = promoterDetails?.approved === 1;

  // Add new state for search
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    // Ensure promoters are loaded
    if (!promoters?.length) {
      refreshPromoters();
    }
  }, [promoters, refreshPromoters]);

  const formatTimeRange = (startDateTime, endDateTime) => {
    const start = startDateTime.split('T')[1].substring(0, 5);
    const end = endDateTime.split('T')[1].substring(0, 5);
    
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    };

    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC'
    });
  };

  // Fetch promoters for the dropdown
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Filter promoters by selected country
  const filteredPromoters = useMemo(() => {
    if (!promoters || !selectedCountry) return [];
    return promoters.filter(promoter => 
      promoter.CountryID === selectedCountry.CountryID
    );
  }, [promoters, selectedCountry]);

  // Filter events from DataContext
  const promoterEvents = events?.filter(event => 
    Number(event.OrganiserID) === Number(promoterDetails?.linkId) && 
    new Date(event.StartDateTime) >= new Date()
  ) || [];

  // Wrap fetchStats in useCallback
  const fetchStats = useCallback(async () => {
    if (isPromoter && isApproved && promoterDetails?.linkId) {
      try {
        // This endpoint would aggregate both DB and GA4 data
        const statsResponse = await fetch(
          `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/promoterstats/${promoterDetails.linkId}`
        );
        const statsData = await statsResponse.json();
        setStats({
          upcomingEvents: statsData.upcomingEvents,
          eventClicks: statsData.eventClicks,
          icalDownloads: statsData.icalDownloads,
          savedEvents: statsData.savedEvents,
          // Add more metrics as needed
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    }
  }, [isPromoter, isApproved, promoterDetails]);

  // Use fetchStats in useEffect
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handlePromoterCreated = async () => {
    await refreshPromoters();
    setShowAddPromoter(false);
  };

  const handleLinkRequest = async () => {
    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/promoterlink',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: userSession.getUserId(),
            organiserId: selectedPromoter
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit link request');
      }

      const data = await response.json();
      const currentUser = userSession.getUser();
      const updatedUser = {
        ...currentUser,
        userTypes: [...currentUser.userTypes, 4],
        userTypeDetails: [...currentUser.userTypeDetails, data.userTypeDetails]
      };
      userSession.setUser(updatedUser);

      // Force component to re-render
      setSelectedPromoter('');
    } catch (err) {
      console.error('Error submitting link request:', err);
    }
  };

  const handleEventCreated = () => {
    // Now fetchStats is accessible here
    fetchStats();
  };

  const handleRemoveEvent = async (eventId) => {
    setEventToDelete(eventId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/events/${eventToDelete}?organiserId=${promoterDetails.linkId}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete event');
      }

      // Refresh events and stats
      await refreshEvents();
      fetchStats();
    } catch (err) {
      console.error('Error removing event:', err);
    }
  };

  const handleEditEvent = (event) => {
    setEventToEdit(event);
    setShowEditEvent(true);
  };

  // Get promoter details from context using linkId
  const businessName = promoterData?.BusinessName || '';

  // Filter promoters based on search term
  const filteredSuggestions = useMemo(() => {
    if (!searchTerm) return [];
    return filteredPromoters.filter(promoter => 
      promoter.BusinessName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [filteredPromoters, searchTerm]);

  // Add click outside handler to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add this function to handle copying the link
  const handleCopyDirectLink = () => {
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000'
      : 'https://driftcal.net';
      
    const encodedBusinessName = encodeURIComponent(businessName);
    const directLink = `${baseUrl}/?events=true&promoter=${encodedBusinessName}`;
    
    navigator.clipboard.writeText(directLink).then(() => {
      // Optionally show a success message
      alert('Link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
  };

  if (isPromoter && !isApproved) {
    return (
      <>
        <div className="p-1">
          <div className="text-center">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Application Pending
              </h2>
              <p className="text-sm text-gray-600">
                Your application is pending, you may be contacted directly.
              </p>
            </div>
          </div>
        </div>
        <AddPromoter 
          isOpen={showAddPromoter}
          onClose={() => setShowAddPromoter(false)}
          onPromoterCreated={handlePromoterCreated}
        />
      </>
    );
  }

  if (!isPromoter) {
    return (
      <>
        <div className="p-1">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              You are not currently a promoter
            </h2>
            <p className="text-sm text-gray-600">
              Choose one of the options below to get started
            </p>
          </div>

          <div className="space-y-6">
           

            {/* Link to existing promoter */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Link to existing promoter
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Request to be linked to an existing promoter
              </p>
              
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading promoters...</p>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowSuggestions(true);
                      setSelectedPromoter(''); // Clear selection when typing
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="Search for a promoter..."
                    className="w-full mb-1 rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
                  />
                  
                  {/* Show selected promoter if one is selected */}
                  {selectedPromoter && (
                    <div className="text-sm text-slate-600 mb-2">
                      Selected: {filteredPromoters.find(p => p.OrganiserID === selectedPromoter)?.BusinessName}
                    </div>
                  )}
                  
                  {/* Suggestions dropdown */}
                  {showSuggestions && searchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredSuggestions.length > 0 ? (
                        <ul className="py-1">
                          {filteredSuggestions.map((promoter) => (
                            <li
                              key={promoter.OrganiserID}
                              className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                              onClick={() => {
                                setSelectedPromoter(promoter.OrganiserID);
                                setSearchTerm(promoter.BusinessName);
                                setShowSuggestions(false);
                              }}
                            >
                              {promoter.BusinessName}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No promoters found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <button
                className="w-full bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 transition-colors disabled:bg-gray-300"
                disabled={!selectedPromoter}
                onClick={handleLinkRequest}
              >
                Request Link
              </button>
            </div>
             {/* Register as new promoter */}
             <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Register as a new promoter
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Create a new promoter profile
              </p>
              <button
                className="w-full bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 transition-colors"
                onClick={() => setShowAddPromoter(true)}
              >
                Register New Promoter
              </button>
            </div>
          </div>
        </div>
        <AddPromoter 
          isOpen={showAddPromoter}
          onClose={() => setShowAddPromoter(false)}
          onPromoterCreated={handlePromoterCreated}
        />
      </>
    );
  }

  if (isPromoter && isApproved) {
    return (
      <>
        <div className="p-1">
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg p-4 text-center h-[100px]">
              <div className="text-4xl font-bold text-white">
                {stats?.upcomingEvents || 0}
              </div>
              <div className="text-xs text-white">
                events still to run
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-center h-[100px]">
              <div className="text-4xl font-bold text-white">
                {stats?.eventClicks || 0}
              </div>
              <div className="text-xs text-white">
                event clicks
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg p-4 text-center h-[100px]">
              <div className="text-4xl font-bold text-white">
                {stats?.icalDownloads || 0}
              </div>
              <div className="text-xs text-white">
                iCal downloads
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 text-center h-[100px]">
              <div className="text-4xl font-bold text-white">
                {stats?.savedEvents || 0}
              </div>
              <div className="text-xs text-white">
                saved to DriftCal
              </div>
            </div>
          </div>
          {/* Promoter Name with Logo */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">
              {businessName}
            </h1>
            {promoterData?.organiserImage && (
              <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg p-4 flex items-center justify-center">
                <img 
                  src={promoterData.organiserImage} 
                  alt={`${businessName} logo`}
                  className="h-8 w-auto object-contain"
                />
              </div>
            )}
          </div>

          {/* Promoter Details */}
          <div className="bg-white border rounded-lg p-4 shadow-sm mb-8">
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Contact Details</h3>
              {/* Contact Details */}
              <div className="flex items-center text-sm text-slate-600">
                <span className="w-8 flex justify-center">
                  <UserIcon className="h-5 w-5 text-slate-400" />
                </span>
                <span>{promoterData?.ContactName}</span>
              </div>
              <div className="flex items-center text-sm text-slate-600">
                <span className="w-8 flex justify-center">
                  <PhoneIcon className="h-5 w-5 text-slate-400" />
                </span>
                <span>{promoterData?.ContactPhone}</span>
              </div>
              <div className="flex items-center text-sm text-slate-600">
                <span className="w-8 flex justify-center">
                  <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                </span>
                <a 
                  href={`mailto:${promoterData?.ContactEmail}`}
                  className="hover:text-slate-900"
                >
                  {promoterData?.ContactEmail}
                </a>
              </div>
              {promoterData?.WebsiteLink && (
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <GlobeAltIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <a 
                    href={promoterData.WebsiteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-slate-900"
                  >
                    {promoterData.WebsiteLink}
                  </a>
                </div>
              )}
              {promoterData?.organiserImage && (
                <div className="flex items-center text-sm text-slate-600">
                  <span className="w-8 flex justify-center">
                    <PhotoIcon className="h-5 w-5 text-slate-400" />
                  </span>
                  <a 
                    href={promoterData.organiserImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-slate-900 truncate max-w-[400px]"
                    title={promoterData.organiserImage}
                  >
                    {promoterData.organiserImage}
                  </a>
                </div>
              )}

              {/* Add this button near the top of the promoter details */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleCopyDirectLink}
                  className="flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
                >
                  <ClipboardIcon className="h-4 w-4 mr-2" />
                  Get my direct link
                </button>
              </div>

            </div>

            {/* Edit Button */}
            <div className="flex items-center justify-end mt-4 pt-4 border-t">
              <button
                className="flex items-center text-xs text-slate-600 hover:text-slate-800"
                onClick={() => setShowEditPromoter(true)}
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
            </div>
          </div>

          {/* Pending Promoter Applications - Moved here */}
          <PendingPromoterApplications promoterId={promoterDetails?.linkId} />

          {/* Manage Events Section */}
          <div className="sticky top-[81px] bg-gray-50 z-10">
            <div className="flex items-center justify-between mb-2 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900">Manage Events</h2>
              <button 
                className="flex items-center px-4 py-1 text-xs text-green-700 rounded-md hover:text-green-600"
                onClick={() => setShowAddEvent(true)}
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                Add
              </button>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
          </div>

          {/* Events List */}
          <div className="space-y-4 mt-4">
            {promoterEvents.map(event => (
              <div key={event.EventID} className="bg-white border rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-slate-900">
                  {event.EventLink ? (
                    <a href={event.EventLink} target="_blank" rel="noopener noreferrer">
                      {event.EventType === 'Competition' && event.RoundNum
                        ? `${event.EventName} - Round ${event.RoundNum}`
                        : event.EventName}
                    </a>
                  ) : (
                    event.EventType === 'Competition' && event.RoundNum
                      ? `${event.EventName} - Round ${event.RoundNum}`
                      : event.EventName
                  )}
                </h4>
                {event.EventType && (
                  <p className="text-xs text-slate-500 mt-1">
                    {`${event.BusinessName} - ${event.EventType}`}
                  </p>
                )}
                <div className="flex items-center mt-1 text-sm text-slate-600">
                  <CalendarIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                  <span>
                    {formatDate(event.StartDateTime)}
                  </span>
                </div>
                <div className="flex items-center mt-1 text-sm text-slate-600">
                  <ClockIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                  <span>
                    {formatTimeRange(event.StartDateTime, event.EndDateTime)}
                  </span>
                </div>
                <div className="flex items-center mt-1 text-sm text-slate-600">
                  <MapPinIcon className="h-4 w-4 mr-1.5 text-slate-400" />
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.VenueName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-blue-600"
                  >
                    {event.VenueName}
                  </a>
                </div>
                <div className="flex items-center justify-end mt-2 pt-2 border-t">
                  <button
                    className="flex items-center text-xs text-slate-600 hover:text-slate-800 mr-4"
                    onClick={() => handleEditEvent(event)}
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    className="flex items-center text-xs text-red-600 hover:text-red-800"
                    onClick={() => handleRemoveEvent(event.EventID)}
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <AddPromoter 
          isOpen={showAddPromoter}
          onClose={() => setShowAddPromoter(false)}
          onPromoterCreated={handlePromoterCreated}
        />
        <AddEvent 
          isOpen={showAddEvent}
          onClose={() => setShowAddEvent(false)}
          onEventCreated={handleEventCreated}
        />
        <ConfirmDialog 
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Event"
          message="Are you sure you want to delete this event? This action cannot be undone."
        />
        <EditEvent 
          isOpen={showEditEvent}
          onClose={() => setShowEditEvent(false)}
          onEventUpdated={handleEventCreated}
          eventData={eventToEdit}
        />
        <EditPromoter 
          isOpen={showEditPromoter}
          onClose={() => setShowEditPromoter(false)}
          onPromoterUpdated={() => {
            setShowEditPromoter(false);
            refreshPromoters();
          }}
          promoterData={promoterData}
        />
      </>
    );
  }

  return null;
} 